import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import {
  operationalIndicatorsService,
  OperationalExportFormat,
  OperationalIndicatorMetric,
  OperationalIndicatorsDashboard,
} from '../../services/operationalIndicatorsService';
import { userService } from '../../services/userService';
import { User } from '../../types/users';
import {
  LEAD_TEMPERATURE_CODES,
  LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS,
  formatLeadTemperatureLabel,
  isLeadTemperatureCode,
} from '../../utils/leadTemperatureDisplay';

type PeriodPreset = 'today' | '7d' | '30d' | 'custom';

const maxRangeDays = 93;

const toIsoDateInput = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftIsoDate = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDateInput(date);
};

const clampCustomRange = (from: string, to: string): { from: string; to: string } => {
  if (!from || !to)
  {
    return { from, to };
  }

  let nextTo = to;
  if (from > nextTo)
  {
    nextTo = from;
  }

  const maxTo = shiftIsoDate(from, maxRangeDays - 1);
  if (nextTo > maxTo)
  {
    nextTo = maxTo;
  }

  return { from, to: nextTo };
};

const startOfUtcDay = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const resolvePeriod = (
  preset: PeriodPreset,
  customFrom: string,
  customTo: string
): { fromUtc?: string; toUtc?: string } => {
  const now = new Date();
  const today = startOfUtcDay(now);

  if (preset === 'today')
  {
    return {
      fromUtc: today.toISOString(),
      toUtc: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    };
  }

  if (preset === '7d')
  {
    const from = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    return {
      fromUtc: from.toISOString(),
      toUtc: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    };
  }

  if (preset === '30d')
  {
    const from = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
    return {
      fromUtc: from.toISOString(),
      toUtc: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    };
  }

  if (!customFrom || !customTo)
  {
    return {};
  }

  const from = new Date(`${customFrom}T00:00:00.000Z`);
  const toExclusive = new Date(`${customTo}T00:00:00.000Z`);
  toExclusive.setUTCDate(toExclusive.getUTCDate() + 1);

  return {
    fromUtc: from.toISOString(),
    toUtc: toExclusive.toISOString(),
  };
};

const formatMetricValue = (metric: OperationalIndicatorMetric): string => {
  if (metric.availability === 'no_data_source')
  {
    return 'Sem fonte';
  }

  if (metric.availability === 'insufficient_data' || metric.value == null)
  {
    return '—';
  }

  if (metric.unit === 'percent')
  {
    return `${metric.value.toFixed(1)}%`;
  }

  if (metric.unit === 'seconds')
  {
    return `${metric.value.toFixed(1)}s`;
  }

  if (metric.unit === 'minutes')
  {
    return `${metric.value.toFixed(1)} min`;
  }

  return String(metric.value);
};

const formatTargetHint = (metric: OperationalIndicatorMetric): string => {
  if (metric.targetValue == null)
  {
    return '';
  }

  const op = metric.comparator === 'lt' ? '<' : metric.comparator === 'gt' ? '>' : '';
  if (metric.unit === 'percent')
  {
    return `Meta ${op} ${metric.targetValue}%`;
  }

  if (metric.unit === 'seconds')
  {
    return `Meta ${op} ${metric.targetValue}s`;
  }

  if (metric.unit === 'minutes')
  {
    return `Meta ${op} ${metric.targetValue} min`;
  }

  return `Meta ${op} ${metric.targetValue}`;
};

const formatAbandonmentWindow = (
  data: OperationalIndicatorsDashboard | null
): string => {
  if (!data?.autoResolveEnabled || !data.autoResolveInactivityMinutes || data.autoResolveInactivityMinutes <= 0)
  {
    return 'Auto-fechamento desligado';
  }

  return `Janela de auto-fechamento: ${data.autoResolveInactivityMinutes} min`;
};

interface OperationalIndicatorsSectionProps {
  refreshToken: number;
}

export const OperationalIndicatorsSection: React.FC<OperationalIndicatorsSectionProps> = ({
  refreshToken,
}) => {
  const { addToast } = useToast();
  const [preset, setPreset] = useState<PeriodPreset>('today');
  const [customFrom, setCustomFrom] = useState(() => toIsoDateInput(new Date()));
  const [customTo, setCustomTo] = useState(() => toIsoDateInput(new Date()));
  const [dashboard, setDashboard] = useState<OperationalIndicatorsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [leadTemperature, setLeadTemperature] = useState('');
  const debounceRef = useRef<number | null>(null);

  const periodParams = useMemo(
    () => resolvePeriod(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const loadDashboard = useCallback(async () => {
    if (preset === 'custom' && (!periodParams.fromUtc || !periodParams.toUtc))
    {
      return;
    }

    setLoading(true);
    try
    {
      const data = await operationalIndicatorsService.getDashboard(periodParams);
      setDashboard(data);
    }
    catch (error)
    {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Erro ao carregar indicadores operacionais';
      addToast(message, 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast, periodParams, preset]);

  useEffect(() => {
    void userService.getUsers('', 1, 200).then((result) => {
      setUsers(result.users ?? []);
    }).catch(() => {
      setUsers([]);
    });
  }, []);

  useEffect(() => {
    if (debounceRef.current != null)
    {
      window.clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      void loadDashboard();
    }, 300);

    return () => {
      if (debounceRef.current != null)
      {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [loadDashboard, refreshToken]);

  const handleExport = async (format: OperationalExportFormat) => {
    if (!periodParams.fromUtc || !periodParams.toUtc)
    {
      addToast('Informe o intervalo de datas para exportar', 'error');
      return;
    }

    setExporting(true);
    try
    {
      await operationalIndicatorsService.export({
        fromUtc: periodParams.fromUtc,
        toUtc: periodParams.toUtc,
        assigneeId: assigneeId || undefined,
        leadTemperature: leadTemperature || undefined,
        format,
      });
    }
    catch (error)
    {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Erro ao exportar dados operacionais';
      addToast(message, 'error');
    }
    finally
    {
      setExporting(false);
    }
  };

  const bigNumberMetrics = dashboard?.metrics.filter((m) => m.chartKind !== 'distribution') ?? [];
  const distributionMetric = dashboard?.metrics.find((m) => m.chartKind === 'distribution');
  const distributionTotal =
    distributionMetric?.distribution?.reduce((sum, bucket) => sum + bucket.count, 0) ?? 0;

  return (
    <div className="bg-white rounded-lg shadow p-5 space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Indicadores operacionais</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Métricas do POP no período selecionado
            {dashboard ? ` · ${dashboard.cohortCount} conversas` : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {([
            ['today', 'Hoje'],
            ['7d', '7 dias'],
            ['30d', '30 dias'],
            ['custom', 'Intervalo'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPreset(value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                preset === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {preset === 'custom' && (
        <div className="flex flex-wrap gap-3">
          <label className="text-sm text-gray-600">
            De
            <input
              type="date"
              value={customFrom}
              min={customTo ? shiftIsoDate(customTo, -(maxRangeDays - 1)) : undefined}
              max={customTo || undefined}
              onChange={(e) => {
                const next = clampCustomRange(e.target.value, customTo);
                setCustomFrom(next.from);
                setCustomTo(next.to);
              }}
              className="ml-2 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-sm text-gray-600">
            Até
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              max={customFrom ? shiftIsoDate(customFrom, maxRangeDays - 1) : undefined}
              onChange={(e) => {
                const next = clampCustomRange(customFrom, e.target.value);
                setCustomFrom(next.from);
                setCustomTo(next.to);
              }}
              className="ml-2 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-end gap-3 border-t border-gray-100 pt-4">
        <label className="text-sm text-gray-600 flex-1 min-w-[180px]">
          Operador (exportação)
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">Todos</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 flex-1 min-w-[180px]">
          Temperatura (exportação)
          <select
            value={leadTemperature}
            onChange={(e) => setLeadTemperature(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            {LEAD_TEMPERATURE_CODES.map((code) => (
              <option key={code} value={code}>
                {LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS[code]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={() => void handleExport('csv')}
          >
            CSV
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={() => void handleExport('xlsx')}
          >
            Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {bigNumberMetrics.map((metric) => {
              const meets = metric.meetsTarget;
              const borderClass =
                metric.availability === 'no_data_source'
                  ? 'border-amber-200'
                  : meets === true
                    ? 'border-emerald-200'
                    : meets === false
                      ? 'border-rose-200'
                      : 'border-gray-100';

              return (
                <div
                  key={metric.code}
                  className={`rounded-lg border ${borderClass} bg-gray-50 p-4`}
                  title={metric.description}
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {metric.name}
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">
                    {formatMetricValue(metric)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{formatTargetHint(metric)}</p>
                  {metric.numerator != null && metric.denominator != null && (
                    <p className="mt-1 text-xs text-gray-400">
                      {metric.numerator}/{metric.denominator}
                    </p>
                  )}
                  {metric.sampleCount != null && metric.numerator == null && (
                    <p className="mt-1 text-xs text-gray-400">
                      Amostra: {metric.sampleCount}
                    </p>
                  )}
                  {metric.code === 'first_response_time'
                    && metric.secondaryValue != null
                    && metric.availability === 'available' && (
                    <p className="mt-1 text-xs text-gray-400">
                      Dentro de 60s: {metric.secondaryValue.toFixed(0)}%
                    </p>
                  )}
                  {metric.code === 'abandonment_rate' && (
                    <p className="mt-1 text-xs text-gray-400">
                      {formatAbandonmentWindow(dashboard)}
                    </p>
                  )}
                  {metric.availability === 'no_data_source' && (
                    <p className="mt-1 text-xs text-amber-700">
                      Sem fonte confiável nesta versão (integração futura)
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {distributionMetric && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-900">{distributionMetric.name}</p>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">{distributionMetric.description}</p>
              {distributionMetric.availability !== 'available'
                || !distributionMetric.distribution
                || distributionTotal === 0 ? (
                <p className="text-sm text-gray-500">Sem classificação no período.</p>
              ) : (
                <div className="space-y-2">
                  {distributionMetric.distribution.map((bucket) => {
                    const pct = distributionTotal > 0
                      ? (bucket.count * 100) / distributionTotal
                      : 0;
                    return (
                      <div key={bucket.slug}>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>
                            {isLeadTemperatureCode(bucket.slug)
                              ? formatLeadTemperatureLabel(bucket.slug, null)
                              : bucket.slug}
                          </span>
                          <span>
                            {bucket.count} ({pct.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
